import type {
  CreditDebtAggregation,
  CreditDebtAggregationType,
  CreditReportAccount,
  CreditReportDocument,
  CreditReportSummaryPayload,
  CreditScoreHistoryPoint,
  CreditSubscriptionPayload,
  CreditSubscriptionStatus,
} from '../credit-overview.model';

const CREDIT_STATUSES = new Set<CreditSubscriptionStatus>([
  'NOT_STARTED',
  'TERMS_ACCEPTED',
  'VERIFY_DIT',
  'VERIFY_SMFA',
  'VERIFY_DIT_FAILED',
  'VERIFY_SMFA_FAILED',
  'VERIFY_SMFA_TIMEDOUT',
  'ENROLLED',
  'FULFILLED',
]);

const AGGREGATION_TYPES = new Set<CreditDebtAggregationType>([
  'CREDIT_CARD',
  'FLEXIBLE_CREDIT_CARD',
  'SECURED_CREDIT_CARD',
  'AUTO',
  'EDUCATION_LOAN',
  'SECURED',
  'UNSECURED',
]);

export class CreditStatusContractError extends Error {
  override readonly name = 'CreditStatusContractError';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapPayload(raw: unknown, operation: string): unknown {
  if (!isRecord(raw) || !isRecord(raw['result'])) {
    throw new Error(
      `Credit ${operation} response is missing its result envelope`,
    );
  }
  const result = raw['result'];
  if (result['status'] !== 200 || result['code'] !== 'SERVICE_OK') {
    throw new Error(
      `Credit ${operation} API returned ${String(result['code'])}`,
    );
  }
  return raw['payload'];
}

function isNullableNumber(value: unknown): value is number | null {
  return (
    value === null || (typeof value === 'number' && Number.isFinite(value))
  );
}

function isNumericString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isFinite(Number(value))
  );
}

function isRealIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function normalizeHistoryPoint(value: unknown): CreditScoreHistoryPoint | null {
  if (
    !isRecord(value) ||
    !isNullableNumber(value['score']) ||
    (value['scoreRange'] !== null && typeof value['scoreRange'] !== 'string') ||
    (value['date'] !== null && !isRealIsoDate(value['date']))
  ) {
    return null;
  }
  return {
    score: value['score'],
    scoreRange: value['scoreRange'],
    date: value['date'],
  };
}

function normalizeAggregation(value: unknown): CreditDebtAggregation | null {
  if (
    !isRecord(value) ||
    !AGGREGATION_TYPES.has(value['type'] as CreditDebtAggregationType) ||
    !isNumericString(value['percentage']) ||
    Number(value['percentage']) < 0 ||
    Number(value['percentage']) > 100
  ) {
    return null;
  }
  return {
    type: value['type'] as CreditDebtAggregationType,
    percentage: value['percentage'],
  };
}

function normalizeAccount(value: unknown): CreditReportAccount {
  const account = isRecord(value) ? value : {};
  const lender =
    typeof account['lender'] === 'string' && account['lender'].trim()
      ? account['lender'].trim()
      : null;
  const type =
    typeof account['type'] === 'string' && account['type'].trim()
      ? account['type'].trim()
      : null;
  const balance =
    typeof account['balance'] === 'number' &&
    Number.isFinite(account['balance'])
      ? account['balance']
      : null;
  const creditLimit = isNullableNumber(account['creditLimit'])
    ? account['creditLimit']
    : null;
  const utilizationRate = isNullableNumber(account['utilizationRate'])
    ? account['utilizationRate']
    : null;
  const minPayment = isNullableNumber(account['minPayment'])
    ? account['minPayment']
    : null;
  const originDate = isRealIsoDate(account['originDate'])
    ? account['originDate']
    : null;
  const status =
    typeof account['status'] === 'string' && account['status'].trim()
      ? account['status'].trim()
      : null;
  const hasValidOriginDate =
    account['originDate'] === null || isRealIsoDate(account['originDate']);
  const hasDataIssue =
    !isRecord(value) ||
    lender === null ||
    type === null ||
    balance === null ||
    !isNullableNumber(account['creditLimit']) ||
    !isNullableNumber(account['utilizationRate']) ||
    !isNullableNumber(account['minPayment']) ||
    !hasValidOriginDate ||
    status === null;
  return {
    lender,
    type,
    balance,
    creditLimit,
    utilizationRate,
    minPayment,
    originDate,
    status,
    ...(hasDataIssue ? { hasDataIssue: true } : {}),
  };
}

export function normalizeCreditStatusResponse(
  raw: unknown,
  expectedGuid: string,
): CreditSubscriptionPayload {
  try {
    const payload = unwrapPayload(raw, 'status');
    if (
      !isRecord(payload) ||
      typeof payload['guid'] !== 'string' ||
      payload['guid'].trim() === ''
    ) {
      throw new Error('Credit status response has an invalid guid');
    }
    if (
      payload['guid'].trim().toLowerCase() !== expectedGuid.trim().toLowerCase()
    ) {
      throw new Error(
        'Credit status response guid does not match requested client',
      );
    }
    const status = payload['status'];
    if (!CREDIT_STATUSES.has(status as CreditSubscriptionStatus)) {
      throw new Error(
        `Credit status response contains unknown status ${String(status)}`,
      );
    }
    return { subscription: { status: status as CreditSubscriptionStatus } };
  } catch (error) {
    throw new CreditStatusContractError(
      error instanceof Error
        ? error.message
        : 'Credit status response is invalid',
    );
  }
}

export function normalizeCreditSummaryResponse(
  raw: unknown,
): CreditReportSummaryPayload {
  const payload = unwrapPayload(raw, 'summary');
  if (!isRecord(payload) || !isRecord(payload['debt'])) {
    throw new Error('Credit summary response has no debt payload');
  }
  const debt = payload['debt'];
  const summary = debt['summary'];
  const accounts = debt['accounts'];
  if (
    !isRecord(summary) ||
    !isNumericString(summary['totalDebt']) ||
    typeof summary['totalDebtAccounts'] !== 'number' ||
    !Array.isArray(summary['aggregation']) ||
    !Array.isArray(accounts)
  ) {
    throw new Error('Credit summary response has an invalid debt shape');
  }

  const rawScore = payload['creditScore'];
  let creditScore: CreditReportSummaryPayload['creditScore'] = null;
  if (rawScore !== undefined && rawScore !== null) {
    if (!isRecord(rawScore)) {
      throw new Error('Credit summary response has an invalid credit score');
    }
    const score = rawScore['score'];
    const scoreRange = rawScore['scoreRange'];
    const date = rawScore['date'];
    const history = rawScore['scoreHistory'];
    if (
      (score !== undefined && !isNullableNumber(score)) ||
      (scoreRange !== undefined &&
        scoreRange !== null &&
        typeof scoreRange !== 'string') ||
      (date !== undefined && date !== null && !isRealIsoDate(date)) ||
      (history !== undefined && !Array.isArray(history))
    ) {
      throw new Error('Credit summary response has an invalid credit score');
    }
    const scoreHistory = (history ?? [])
      .map(normalizeHistoryPoint)
      .filter((point): point is CreditScoreHistoryPoint => point !== null);
    const scoreHistoryIssueCount = (history ?? []).length - scoreHistory.length;
    creditScore = {
      score: score as number | null | undefined,
      scoreRange: scoreRange as string | null | undefined,
      date: date as string | null | undefined,
      scoreHistory,
      ...(scoreHistoryIssueCount ? { scoreHistoryIssueCount } : {}),
    };
  }

  const aggregation = summary['aggregation']
    .map(normalizeAggregation)
    .filter((item): item is CreditDebtAggregation => item !== null);
  const aggregationIssueCount =
    summary['aggregation'].length - aggregation.length;

  return {
    creditScore,
    debt: {
      summary: {
        totalDebt: summary['totalDebt'],
        totalDebtAccounts: summary['totalDebtAccounts'],
        aggregation,
        ...(aggregationIssueCount ? { aggregationIssueCount } : {}),
      },
      accounts: accounts.map(normalizeAccount),
    },
  };
}

export async function normalizeCreditReportResponse(
  raw: unknown,
): Promise<CreditReportDocument> {
  if (!(raw instanceof Blob) || raw.size === 0) {
    throw new Error('Credit report response is not a PDF document');
  }
  const bytes = new Uint8Array(await _readBlob(raw));
  if (_hasPdfSignature(bytes)) {
    return raw.type.toLowerCase().startsWith('application/pdf')
      ? raw
      : new Blob([bytes], { type: 'application/pdf' });
  }

  let encoded = new TextDecoder().decode(bytes).trim();
  if (encoded.startsWith('"') || encoded.startsWith('{')) {
    try {
      const parsed = JSON.parse(encoded) as unknown;
      const payload = isRecord(parsed)
        ? unwrapPayload(parsed, 'report')
        : parsed;
      if (typeof payload !== 'string') {
        throw new Error('Credit report payload is not a base64 string');
      }
      encoded = payload;
    } catch {
      throw new Error('Credit report response is not a PDF document');
    }
  }
  encoded = encoded
    .replace(/^data:application\/pdf;base64,/, '')
    .replace(/\s/g, '');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)) {
    throw new Error('Credit report response is not a PDF document');
  }
  let binary: string;
  try {
    binary = atob(encoded);
  } catch {
    throw new Error('Credit report response is not a PDF document');
  }
  const decoded = Uint8Array.from(binary, (character) =>
    character.charCodeAt(0),
  );
  if (!_hasPdfSignature(decoded)) {
    throw new Error('Credit report response is not a PDF document');
  }
  return new Blob([decoded], { type: 'application/pdf' });
}

function _hasPdfSignature(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}

function _readBlob(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}
